import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Seo, { SITE_URL } from '../components/Seo';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const RocketIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

/* ─── CIRCUIT BG ──────────────────────────────────────────── */
const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="980" y1="120" x2="1400" y2="120" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
        <circle cx="570" cy="420" r="5" fill={V1} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollStatus, setEnrollStatus] = useState(null); // null | 'pending' | 'payment_verified' | 'approved'

  useEffect(() => {
    API.get(`/courses/${id}`).then(res => setCourse(res.data)).finally(() => setLoading(false));
  }, [id]);

  // Check if user already has an active application
  useEffect(() => {
    if (user) {
      API.get(`/applications/check/${id}`)
        .then(res => { if (res.data.hasActive) setEnrollStatus(res.data.status); })
        .catch(() => {});
    }
  }, [id, user]);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 16, background: 'var(--bg)', color: 'var(--muted)' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite' }} />
      Loading course...
    </div>
  );
  if (!course) return (
    <div style={{ textAlign: 'center', padding: '160px 0', background: 'var(--bg)', color: 'var(--muted)' }}>
      <Seo title="Course Not Found" path={`/courses/${id}`} noindex />
      Course not found. <Link to="/courses" style={{ color: V2, fontWeight: 700 }}>Browse all courses</Link>
    </div>
  );

  const handleEnroll = () => {
    if (!user) { navigate('/login'); return; }
    navigate(`/apply/${course._id}`);
  };

  const seatsLeft = course.totalSeats - course.enrolledCount;

  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description,
    provider: { '@type': 'EducationalOrganization', name: 'ThiranX', sameAs: SITE_URL },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Online',
      courseWorkload: course.duration,
    },
    offers: { '@type': 'Offer', price: course.fee, priceCurrency: 'INR', availability: 'https://schema.org/InStock' },
  };

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      <Seo
        title={course.title}
        description={course.description?.slice(0, 160) || `Learn ${course.title} with ThiranX — industry-focused curriculum and a verified certificate on completion.`}
        path={`/courses/${course._id}`}
        schema={courseSchema}
      />

      {/* ══ HERO ════════════════════════════════════════════ */}
      <section style={{
        padding: '120px 0 80px', position: 'relative', overflow: 'hidden',
        background: 'var(--bg)',
      }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          {/* Breadcrumb */}
          <Link to="/courses" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--muted)', fontSize: 13.5, fontWeight: 600, marginBottom: 36,
            transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = V2}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
          >
            <ArrowLeftIcon /> Back to Courses
          </Link>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 48, alignItems: 'start' }} className="detail-grid">
            {/* LEFT */}
            <div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex', padding: '5px 14px', borderRadius: 999,
                  fontSize: 12, fontWeight: 700, color: 'var(--badge-pill-text)',
                  background: 'var(--badge-pill-bg)', border: '1px solid var(--badge-pill-border)',
                }}>
                  {course.category}
                </span>
                <span style={{
                  display: 'inline-flex', padding: '5px 14px', borderRadius: 999,
                  fontSize: 12, fontWeight: 700, color: 'var(--success)',
                  background: 'rgba(13,186,121,0.12)', border: '1px solid rgba(13,186,121,0.25)',
                }}>
                  {course.level}
                </span>
              </div>

              <h1 style={{
                fontSize: 'clamp(28px,4vw,50px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 20,
                fontFamily: "'Inter',sans-serif",
              }}>{course.title}</h1>

              <p style={{ color: 'var(--text-2)', fontSize: 17, lineHeight: 1.78, maxWidth: 600, marginBottom: 36 }}>
                {course.description}
              </p>

              {/* Meta chips */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {[
                  { icon: <ClockIcon />, label: 'Duration', value: course.duration },
                  { icon: <UserIcon />, label: 'Instructor', value: course.instructor },
                  { icon: <UsersIcon />, label: 'Seats Left', value: `${seatsLeft} of ${course.totalSeats}` },
                ].map(({ icon, label, value }, i) => (
                  <div key={i} style={{
                    background: 'var(--card-solid)', border: '1px solid var(--border)',
                    borderRadius: 12, padding: '14px 20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--muted)', fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                      <span style={{ color: V2 }}>{icon}</span>{label}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: Price card */}
            <div style={{
              background: 'var(--card-solid)', border: '1px solid var(--border)',
              borderRadius: 24, overflow: 'hidden',
              boxShadow: 'var(--shadow-lg)',
              position: 'sticky', top: 100,
            }}>
              <div style={{ height: 5, background: `linear-gradient(90deg,${V1},${V2})` }} />
              <div style={{ padding: '28px 28px 32px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Course Fee</div>
                <div style={{
                  fontFamily: "'Inter',sans-serif", fontWeight: 900,
                  fontSize: 52, color: 'var(--text)', lineHeight: 1, marginBottom: 6,
                }}>₹{course.fee.toLocaleString()}</div>
                <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 28 }}>One-time payment via GPay</div>

                {/* Enroll button — disabled if user has active application */}
                {enrollStatus ? (
                  <div style={{
                    width: '100%', padding: '16px', borderRadius: 12,
                    background: 'var(--bg3)', border: '1.5px solid var(--border)',
                    textAlign: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--warning)' }}>Already Enrolled</span>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, lineHeight: 1.5 }}>
                      Status: <strong style={{ color: 'var(--text)', textTransform: 'capitalize' }}>
                        {enrollStatus === 'payment_verified' ? 'Payment Verified' :
                         enrollStatus === 'approved' ? 'Enrolled & Active' : 'Pending Review'}
                      </strong>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 6 }}>You can re-enroll only after this course is completed.</div>
                  </div>
                ) : (
                  <button
                    onClick={handleEnroll}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '16px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg,${V1},${V3})`,
                      color: '#fff', fontWeight: 800, fontSize: 16,
                      fontFamily: "'Satoshi','DM Sans',sans-serif",
                      boxShadow: `0 8px 28px ${V1}44`,
                      transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${V1}55`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = `0 8px 28px ${V1}44`; }}
                  >
                    <RocketIcon /> Enroll Now
                  </button>
                )}
                <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 14 }}>Pay via GPay &amp; submit screenshot</p>

                {/* Mini trust badges */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                  {['Verified certificate on completion', 'Expert Tamil-medium instruction', 'Placement support included'].map((b, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 12.5 }}>
                      <span style={{ color: 'var(--success)', flexShrink: 0 }}><CheckIcon /></span>{b}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SYLLABUS ═════════════════════════════════════════ */}
      {course.syllabus && course.syllabus.length > 0 && (
        <section style={{ padding: '80px 0 120px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
          <div className="container">
            <div className="section-label">CURRICULUM</div>
            <h2 style={{
              fontSize: 'clamp(26px,3.5vw,40px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 40,
              fontFamily: "'Inter',sans-serif",
            }}>
              What You'll <span style={{
                background: `linear-gradient(135deg,${V1},${V2})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              }}>Learn</span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {course.syllabus.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  background: 'var(--card-solid)', border: '1px solid var(--border)',
                  borderRadius: 12, padding: '16px 20px',
                  transition: 'border-color 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: `linear-gradient(135deg,${V1},${V3})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', marginTop: 1,
                  }}>
                    <CheckIcon />
                  </span>
                  <span style={{ color: 'var(--text-2)', fontSize: 14.5, lineHeight: 1.55 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default CourseDetail;
